import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, MessageCircle, Globe, Send } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/PageHero";
import { Reveal, Overline } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { IMG, WHATSAPP_NUMBER, WHATSAPP_DISPLAY, EMAIL } from "@/data/content";

const inputCls = "border-white/10 bg-white/5 text-white placeholder:text-slate-500 focus-visible:border-[#FF4500] focus-visible:ring-[#FF4500]/30";

export default function Contact() {
  useSeo({
    title: "Contact | Imperium Group",
    description: "Neem contact op met Imperium Group. Benieuwd wat wij voor jouw marketing kunnen betekenen? Bel, app of mail ons — wij denken graag met je mee.",
    path: "/contact",
  });

  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", message: "", inquiry_type: "Contact" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Vul minimaal naam, e-mail en bericht in."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Bedankt voor je bericht! Wij nemen zo snel mogelijk contact op.");
      setForm({ name: "", company: "", email: "", phone: "", message: "", inquiry_type: "Contact" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="contact-page">
      <PageHero
        overline="Contact"
        title="Laten we jouw marketing bespreken"
        subtitle="Van een losse campagne tot een complete 360°-aanpak. Neem contact op en wij denken graag met je mee."
        image={IMG.team}
      />

      <section className="pb-24">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 lg:grid-cols-5 lg:px-10">
          <Reveal className="lg:col-span-2">
            <Overline>Direct contact</Overline>
            <h2 className="mt-4 font-display text-2xl font-semibold text-white lg:text-3xl">Start een gesprek</h2>
            <div className="mt-8 space-y-4">
              <a href={`https://wa.me/${WHATSAPP_NUMBER}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#12151D] p-5 transition-colors hover:border-[#FF4500]/50" data-testid="contact-whatsapp-link">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF4500]/10 text-[#FF4500]"><MessageCircle className="h-5 w-5" /></span>
                <div><div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">WhatsApp</div><div className="text-white">{WHATSAPP_DISPLAY}</div></div>
              </a>
              <a href={`mailto:${EMAIL}`} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#12151D] p-5 transition-colors hover:border-[#FF4500]/50" data-testid="contact-email-link">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF4500]/10 text-[#FF4500]"><Mail className="h-5 w-5" /></span>
                <div><div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">E-mail</div><div className="text-white">{EMAIL}</div></div>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-[#12151D] p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#FF4500]/10 text-[#FF4500]"><Globe className="h-5 w-5" /></span>
                <div><div className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Website</div><div className="text-white">www.imperiumgroup.nl</div></div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-[#12151D] p-8 lg:p-10" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field label="Naam *"><Input data-testid="contact-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Jouw naam" /></Field>
                <Field label="Bedrijfsnaam"><Input data-testid="contact-company" className={inputCls} value={form.company} onChange={set("company")} placeholder="Bedrijfsnaam" /></Field>
                <Field label="E-mail *"><Input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="naam@bedrijf.nl" /></Field>
                <Field label="Telefoon"><Input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="Telefoon" /></Field>
              </div>
              <div className="mt-5"><Field label="Bericht *"><Textarea data-testid="contact-message" rows={5} className={inputCls} value={form.message} onChange={set("message")} placeholder="Waarmee kunnen wij je helpen?" /></Field></div>
              <button type="submit" disabled={loading} data-testid="contact-submit" className="mt-8 inline-flex items-center gap-2 rounded-xl bg-[#FF4500] px-8 py-4 text-base font-semibold text-[#090B0E] transition-[background-color,transform] duration-200 hover:bg-[#ff6428] hover:scale-[1.02] active:scale-95 disabled:opacity-60">
                {loading ? "Bezig..." : "Verstuur bericht"} <Send className="h-4 w-4" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (<div className="space-y-2"><Label className="text-sm text-slate-300">{label}</Label>{children}</div>);
}
